import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import CtaButton from '../../ctaButton/ctaButton';
import heroPrivacySecurityPd from '../images/heroPrivacySecurityPd.svg';
import Tag from '../../tag/tag';
import styles from './heroHomePrequalCard.module.scss';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { capitalizeFirstLetter } from '../../../utils/util';
import SmartImage from '../../smartImage/smartImage';
import {
  getTemplateTokens,
  resolveLinkTemplate,
} from '../../../services/helpers/linkTemplate';

function HeroHomePrequalCard({
  loanPurposes = null,
  contentNote = '',
  contentEmail = null,
  headline = '',
  theme = '',
  experimentName,
  experimentVariation,
  privacyImage,
  isPersonalization = false,
}) {
  const { width } = useWindowDimensions();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (width < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [width]);

  const heroEmailTrack = (analyticsKey) => {
    trackHeapEvent(
      {
        Name: 'Home Page Hero - Respond to Mail Offer',
        CTA: analyticsKey,
      },
      'CMS_Unification'
    );
  };

  const handleSubmit = (title, loanPurpose, url, heapKey) => {
    const source = new URLSearchParams(window.location.search);

    // Collect every {{Token}} referenced by ANY card's CTA link. These tokens are
    // injected into their own card's URL, so they must NOT be forwarded raw by any
    // card (a token consumed by card A is also stripped from card B's params).
    const consumedTokens = new Set(
      (loanPurposes ?? []).flatMap((purpose) =>
        getTemplateTokens(purpose.ctaButton?.link ?? '')
      )
    );

    // Fill this card's own {{Token}} placeholders from the current URL params.
    const resolvedLink = resolveLinkTemplate(url ?? '', source);

    // Forward remaining params: everything except the templated tokens.
    const params = new URLSearchParams(window.location.search);
    consumedTokens.forEach((token) => params.delete(token));

    if (loanPurpose) {
      params.set('loanPurpose', loanPurpose);
    }

    if (experimentName && experimentVariation) {
      params.set(
        'experiment',
        `${experimentName}__${capitalizeFirstLetter(experimentVariation)}`
      );
    }

    const queryString = params.toString();
    const separator = resolvedLink.includes('?') ? '&' : '?';
    const newURL = queryString
      ? `${resolvedLink}${separator}${queryString}`
      : resolvedLink;
    trackHeapEvent(
      {
        'Card Name': title,
        CTA: heapKey,
      },
      'Hero-Card-Click'
    );
    window.location.assign(newURL);
  };

  const renderEmailLink = () =>
    contentEmail && (
      <div className={styles.heroPrequalCardActionsMail}>
        <div className={styles.heroPrequalActionsMailLinkWrapper}>
          <a
            data-testid="email-offer"
            href={contentEmail.link}
            onClick={() => heroEmailTrack(contentEmail.heapEventKey)}
            className={`${styles.heroPrequalCardActionsMailLink} ${
              isPersonalization
                ? styles.heroPersonilaztionPrequalCardTextColor
                : ''
            }`}
          >
            <span
              className={`${styles.contentEmailIcon} ${
                isPersonalization ? styles.personalizationMailLink : ''
              }`}
              aria-hidden="true"
            />
            <div
              className={`${styles.mailLinkText} ${
                isPersonalization ? styles.personalizationMailLinkText : ''
              }`}
            >
              {contentEmail.text}
            </div>
          </a>
        </div>
      </div>
    );

  const renderPrivacyImage = () => (
    <div>
      <SmartImage
        src={heroPrivacySecurityPd.src}
        alt="Privacy & Security"
        loading="lazy"
        width={heroPrivacySecurityPd.width}
        height={heroPrivacySecurityPd.height}
        data-testid="privacy-image"
      />
    </div>
  );

  return (
    <div
      data-testid="hero-prequal-docked"
      className={`${styles.heroPrequal} ${styles.heroPrequalDocked} ${
        theme ? styles.theme : ''
      } ${!isPersonalization && isMobile ? styles.marginTop : ''}`}
    >
      <div className={styles.heroPrequalContainer}>
        {headline && (
          <div
            className={`${styles.heroPrequalHeadline} ${
              isPersonalization ? styles.heroPersonilaztionPrequalHeadline : ''
            }`}
          >
            {parse(headline)}
          </div>
        )}
        {contentNote && (
          <div
            className={`${
              isPersonalization
                ? styles.heroPersonilaztionPrequalCardTextColor
                : styles.heroPrequalCardText
            }`}
          >
            {parse(contentNote)}
          </div>
        )}
        <div className={styles.heroPrequalCardContainer}>
          {loanPurposes?.map((loanPurpose, index) => {
            const cardClick = () =>
              handleSubmit(
                loanPurpose.ctaButton?.title,
                loanPurpose.loanLabel,
                loanPurpose.ctaButton?.link,
                loanPurpose.ctaButton?.heapEventKey
              );

            return (
              <div
                key={loanPurpose.headline}
                className={styles.heroPrequalCard}
                role="button"
                tabIndex={0}
                aria-label={loanPurpose.ctaButton?.label || 'Check Your Rate'}
                onClick={cardClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    cardClick();
                  }
                }}
              >
                <div />
                <div className={styles.heroPrequalCardPromotionWrapper}>
                  {loanPurpose.promotion && (
                    <div>
                      <div className={styles.heroPrequalCardPromotion}>
                        <div>
                          <Tag label={loanPurpose.promotion} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles.heroPrequalCardContentWrapper}>
                    <div className={styles.heroPrequalCardMobileWrapper}>
                      {isMobile && (
                        <div className={styles.heroPrequalCardTitle}>
                          <SmartImage
                            data-testid={`mobile-icon-${index}`}
                            src={loanPurpose?.headlineImage?.fields?.file.url}
                            alt={
                              loanPurpose.headlineImage?.alt ??
                              loanPurpose.headline ??
                              ''
                            }
                            priority
                            width={48}
                            height={48}
                          />
                        </div>
                      )}
                      <div
                        className={styles.heroHomePrequalCardMobileSubWrapper}
                      >
                        <div className={`${styles.heroPrequalCardTitle}`}>
                          {!isMobile && (
                            <SmartImage
                              src={loanPurpose?.headlineImage?.fields?.file.url}
                              alt={
                                loanPurpose.headlineImage?.alt ??
                                loanPurpose.headline ??
                                ''
                              }
                              data-testid={`desktop-icon-${index}`}
                              priority
                              width={48}
                              height={48}
                            />
                          )}
                          <div>{parse(loanPurpose.headline ?? '')}</div>
                        </div>
                        <div className={styles.heroPrequalCardDescription}>
                          {loanPurpose.description}
                        </div>
                        {loanPurpose.ctaButton && (
                          <CtaButton
                            type="submit"
                            data-testid={`check-rate-${index}`}
                            className={`${styles.homePrequalCardsubmitBtn} ${
                              isPersonalization
                                ? styles.personalizationCardButton
                                : ''
                            }`}
                            label={
                              loanPurpose.ctaButton?.label || 'Check Your Rate'
                            }
                            variant={loanPurpose.ctaButton?.variant}
                            color={loanPurpose.ctaButton?.color}
                            aria-label="submit-rate"
                            onClick={(e) => {
                              e.stopPropagation();
                              cardClick();
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className={`${styles.heroPrequalCardFooter} ${
            isPersonalization ? styles.heroPersonilaztionPrequalCardFooter : ''
          }`}
        >
          {!isMobile ? (
            <>
              {renderEmailLink()}
              {privacyImage && renderPrivacyImage()}
            </>
          ) : (
            <div className={styles.mobileFooter}>
              {privacyImage && renderPrivacyImage()}
              {renderEmailLink()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HeroHomePrequalCard;
